import React, { useState } from "react";
import { Card, Col, Row } from "react-bootstrap";
import PageContainer from "Common/Components/PageContainer/PageContainer";
import PageBreadcrumb from "Common/Components/PageBreadcrumb/PageBreadcrumb";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { MecordColors } from "Constants/Color";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import AssignmentReturnIcon from "@mui/icons-material/AssignmentReturn";
import HistoryIcon from "@mui/icons-material/History";
import { toast } from "react-toastify";
import ChallanForm from "./form";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchChallanList, downloadChallanPdf } from "slices/sales_order/challan/thunk";
import { RootState } from "../../../../index";
import { useEffect } from "react";

// ─── Types ─────────────────────────────────────────────────────────────────

interface ChallanRecord {
    challan_id: string;
    challan_no: string;
    challan_date: string;
    initiated_by: string;
    shipping_address_details?: {
        address?: string;
        city?: string;
        state?: string;
        country?: string;
        pincode?: string;
        pin_code?: string;
    };
    delivered_by?: string;
    delivery_date?: string;
    created_by_user?: { username?: string };
    created_at?: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const formatAddress = (addr: ChallanRecord["shipping_address_details"]) => {
    if (!addr) return "-";
    return [addr.address, addr.city, addr.state, addr.pincode || addr.pin_code]
        .filter(Boolean)
        .join(", ");
};

// ─── Component ──────────────────────────────────────────────────────────────

const ChallanIndex: React.FC = () => {
    document.title = "Manage Challan";

    const navigate = useNavigate();
    const { id: salesOrderServiceId } = useParams<{ id: string }>();
    const dispatch = useDispatch<any>();
    const { challanList, loading } = useSelector((state: RootState) => state.Challan);

    // ── UI state ────────────────────────────────────────────────────────────
    const [showForm, setShowForm] = useState(false);
    const [formMode, setFormMode] = useState<"create" | "edit">("create");
    const [editData, setEditData] = useState<any | null>(null);

    useEffect(() => {
        if (salesOrderServiceId) {
            dispatch(fetchChallanList(salesOrderServiceId));
        }
    }, [dispatch, salesOrderServiceId]);

    // ── Open/close helpers ───────────────────────────────────────────────────
    const openCreate = () => {
        setEditData(null);
        setFormMode("create");
        setShowForm(true);
        // Smoothly scroll to the form below the listing
        setTimeout(() => {
            document
                .getElementById("challan-form-section")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    const openEdit = (row: ChallanRecord) => {
        setEditData(row);
        setFormMode("edit");
        setShowForm(true);
        setTimeout(() => {
            document
                .getElementById("challan-form-section")
                ?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 50);
    };

    const closeForm = () => {
        setShowForm(false);
        setEditData(null);
    };

    // ── Shared table styles ──────────────────────────────────────────────────
    const thStyle: React.CSSProperties = {
        padding: "10px 12px",
        borderBottom: "2px solid #eee",
        textAlign: "left",
        fontSize: "0.78rem",
        fontWeight: 600,
        whiteSpace: "nowrap",
        backgroundColor: MecordColors.LightGrayColor,
    };
    const tdStyle: React.CSSProperties = {
        padding: "10px 12px",
        borderBottom: "1px solid #f0f0f0",
        fontSize: "0.78rem",
        verticalAlign: "middle",
    };

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <PageContainer>
            {/* ══════ Page header – breadcrumb ═══════════════════════════════════ */}
            <PageBreadcrumb
                title="Manage Challan"
                titleLink=""
                breadcrumbs={[
                    { label: "Sales Order Services", to: "/sales_order/service/sales_order_log" },
                    { label: "Manage Challan", to: "" },
                ]}
                backNavigationPath={"/sales_order/service/sales_order_log"}
            />

            <hr />

            {/* ══════ Challan Listing ══════════════════════════════════════════════ */}
            <Row id="challanList">
                <Col lg={12}>
                    <Card className="shadow-none">
                        {/* ── Card header ─────────────────────────────────────────────── */}
                        <Card.Header>
                            <div className="d-flex align-items-center justify-content-between">
                                <h6 className="card-title mb-0">Created Challan</h6>

                                {/* Single "Create Challan" button – always in the header */}
                                <button
                                    className="btn btn-sm d-flex align-items-center gap-1"
                                    style={{
                                        backgroundColor: MecordColors?.ThemeBGColor,
                                        color: "#fff",
                                        fontSize: "0.8rem",
                                        borderRadius: "6px",
                                        padding: "6px 14px",
                                    }}
                                    onClick={openCreate}
                                >
                                    <AddIcon style={{ fontSize: 16 }} />
                                    Create Challan
                                </button>
                            </div>
                        </Card.Header>

                        {/* ── Card body / table ────────────────────────────────────────── */}
                        <Card.Body className="mt-2 p-0">
                            {challanList && challanList.length > 0 ? (
                                <div className="table-responsive">
                                    <table
                                        style={{
                                            width: "100%",
                                            borderCollapse: "collapse",
                                            fontSize: "0.78rem",
                                        }}
                                    >
                                        <thead>
                                            <tr>
                                                <th style={thStyle}>S.No</th>
                                                <th style={thStyle}>Challan No.</th>
                                                <th style={thStyle}>Challan Date</th>
                                                <th style={thStyle}>Initiated By</th>
                                                <th style={thStyle}>Created By / Date</th>
                                                <th style={{ ...thStyle, textAlign: "center" }}>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {challanList.map((row: any, index: number) => (
                                                <tr
                                                    key={row.challan_id || row.id || index}
                                                    style={{
                                                        backgroundColor: index % 2 === 0 ? "#fff" : "#fafafa",
                                                    }}
                                                >
                                                    <td style={tdStyle}>{index + 1}</td>
                                                    <td style={tdStyle}>
                                                        <span
                                                            style={{
                                                                fontWeight: 600,
                                                                color: MecordColors?.ThemeBGColor,
                                                            }}
                                                        >
                                                            {row.challan_no || "-"}
                                                        </span>
                                                    </td>
                                                    <td style={tdStyle}>
                                                        <FormattedDate date={row.challan_date} />
                                                    </td>
                                                    <td style={tdStyle}>{row.initiated_by || "-"}</td>
                                                    <td style={tdStyle}>
                                                        <NameWithDate
                                                            name={row.created_by_user?.username}
                                                            date={row.created_at}
                                                        />
                                                    </td>
                                                    <td style={{ ...tdStyle, textAlign: "center" }}>
                                                        <DynamicMenuItem
                                                            row={row}
                                                            menuItems={[
                                                                {
                                                                    icon: VisibilityIcon,
                                                                    label: "View",
                                                                    permission: true,
                                                                    onClick: () => navigate(`/sales_order/service/challan/view/${row.challan_id}`),
                                                                    color: "info",
                                                                },
                                                                {
                                                                    icon: EditIcon,
                                                                    label: "Edit",
                                                                    permission: true,
                                                                    onClick: () => openEdit(row),
                                                                    color: "primary",
                                                                },
                                                                {
                                                                    icon: PictureAsPdfIcon,
                                                                    label: "Download PDF",
                                                                    permission: true,
                                                                    onClick: () => {
                                                                        dispatch(
                                                                            downloadChallanPdf({
                                                                                id: row.challan_id,
                                                                                challanNo: row.challan_no,
                                                                            })
                                                                        )
                                                                            .unwrap()
                                                                            .then(() => toast.success("PDF Downloaded successfully"))
                                                                            .catch((err: any) =>
                                                                                toast.error(err || "Failed to download PDF")
                                                                            );
                                                                    },
                                                                    color: "error",
                                                                },
                                                                {
                                                                    icon: AssignmentReturnIcon,
                                                                    label: "Return Accessories",
                                                                    permission: (menuRow: any) => !!menuRow.can_return,
                                                                    onClick: () =>
                                                                        navigate(`/sales_order/service/challan/return/${row.challan_id}`),
                                                                    color: "warning",
                                                                },
                                                                {
                                                                    icon: HistoryIcon,
                                                                    label: "View Return",
                                                                    permission: (menuRow: any) => !!menuRow.has_returns,
                                                                    onClick: () =>
                                                                        navigate(`/sales_order/service/challan/return-view/${row.challan_id}`),
                                                                    color: "info",
                                                                },
                                                            ]}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                /* ── Empty state ──────────────────────────────────────── */
                                <div
                                    className="d-flex flex-column align-items-center justify-content-center"
                                    style={{
                                        minHeight: 200,
                                        color: "#aaa",
                                        userSelect: "none",
                                    }}
                                >
                                    <svg
                                        width="60"
                                        height="60"
                                        viewBox="0 0 72 72"
                                        fill="none"
                                        xmlns="http://www.w3.org/2000/svg"
                                        style={{ marginBottom: 12, opacity: 0.2 }}
                                    >
                                        <rect
                                            x="12"
                                            y="8"
                                            width="48"
                                            height="56"
                                            rx="4"
                                            stroke="#888"
                                            strokeWidth="3"
                                        />
                                        <line x1="22" y1="24" x2="50" y2="24" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                        <line x1="22" y1="34" x2="50" y2="34" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                        <line x1="22" y1="44" x2="38" y2="44" stroke="#888" strokeWidth="2.5" strokeLinecap="round" />
                                    </svg>
                                    <p style={{ fontSize: "0.88rem", marginBottom: 0 }}>No challan created yet</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* ══════ Create / Edit Form (shown below listing) ═══════════════════════ */}
            {showForm && (
                <div id="challan-form-section" className="mt-4">
                    <ChallanForm
                        mode={formMode}
                        initialData={editData}
                        onSuccess={() => {
                            closeForm();
                            if (salesOrderServiceId) {
                                dispatch(fetchChallanList(salesOrderServiceId));
                            }
                        }}
                        onCancel={closeForm}
                    />
                </div>
            )}
        </PageContainer>
    );
};

export default ChallanIndex;
